import { useStrictLocalization } from "@faire/web--source/common/localization";
import { IGetMessengerConversationsResponse } from "@faire/web-api--source/indigofair/data/IGetMessengerConversationsResponse";
import { IUser } from "@faire/web-api--source/indigofair/data/IUser";
import { createContext, useCallback, useContext, useMemo } from "react";

import {
  getConversationCreatedAt as getConversationModelCreatedAt,
  getConversationToken as getConversationModelToken,
  getConversationUserToken as getConversationModelUserToken,
  getConversationLatestMessage as getConversationModelLatestMessage,
  getConversationOtherPartyBrandToken as getConversationModelOtherPartyBrandToken,
  isConversationUnRead as isConversationModelUnRead,
  getConversationOtherPartyBusinessImage as getConversationModelOtherPartyBusinessImage,
  isConversationSupportConversation as isConversationModelSupportConversation,
  isConversationMarkedAsSpam as isConversationModelMarkedAsSpam,
  isConversationStarredByBrand as isConversationModelStarredByBrand,
  getConversationKey as getConversationModelKey,
  getConversationOrderedMessages as getConversationModelOrderedMessages,
  isConversationInputDisabled as isConversationModelInputDisabled,
  fetchAdditionalMessagesThrottled,
  getConversationOtherPartyUserType as getConversationModelOtherPartyUserType,
  getConversationParticipants as getConversationModelParticipants,
  getBrandCustomer,
  openConversation,
  getConversationLastModified as getConversationModelLastModified,
  isMessageFromCurrentUser as isMessageModelFromCurrentUser,
  isMessageFromOtherUser as isMessageModelFromOtherUser,
  isMessageFromSameRetailerAsCurrentUser as isMessageModelFromSameRetailerAsCurrentUser,
  isMessageFromSameBrandAsCurrentUser as isMessageModelFromSameBrandAsCurrentUser,
  isMessageRead as isMessageModelRead,
  isMessageLatestReadFromCurrentUser as isMessageModelLatestReadFromCurrentUser,
  isMessageLatestDelivered as isMessageModelLatestDelivered,
  sendMessage as sendMessageModel,
  deleteMessage as deleteMessageModel,
  getMessageOtherPartyProfilePicUrl as getMessageModelOtherPartyProfilePicUrl,
  getMessageCurrentPartyProfilePicUrl as getMessageModelCurrentPartyProfilePicUrl,
  getMessageOtherPartyFirstName as getMessageModelOtherPartyFirstName,
} from "./ConversationModel";
import {
  MessageModel,
  getToken as getMessageModelToken,
  shouldPopUp as getMessageModelShouldPopUp,
} from "./MessageModel";
import { MessengerStore } from "./MessengerStore";

interface MessengerStoreContext {
  store?: MessengerStore;
}

const MessengerStoreContext = createContext<MessengerStoreContext>({});

export const MessengerStoreContextProvider = MessengerStoreContext.Provider;

export const useMessengerStore = () => {
  const { store } = useContext(MessengerStoreContext);
  if (!store) {
    // eslint-disable-next-line no-console
    console.warn("MessengerStore not initialized in useMessengerStore");
  }
  return store!;
};

export const useMessengerStoreOrUndefined = () => {
  const { store } = useContext(MessengerStoreContext);
  return store;
};

const getUseFields = <T extends (keyof MessengerStore)[]>(
  _: T
): (() => Pick<MessengerStore, T[number]>) => {
  return useMessengerStore;
};

// Conversation List Hooks
const useMessengerStoreConversationListFields = getUseFields([
  "orderedConversationIdentifiers",
  "hasConversations",
  "isLoadingConversations",
  "unreadConversationCount",
  "setTotalUnreadConversations",
  "starredByBrandConversationCount",
  "showOnlyUnreadConversations",
  "showOnlyStarredByBrandConversations",
  "setShowOnlyUnreadConversations",
  "setShowOnlyStarredByBrandConversations",
  "setSearch",
  "nextConversations",
  "query",
  "conversationsNextRequest",
]);

export const useMessengerStoreConversationList = () => {
  const {
    orderedConversationIdentifiers,
    hasConversations,
    isLoadingConversations,
    unreadConversationCount,
    setTotalUnreadConversations,
    starredByBrandConversationCount,
    showOnlyUnreadConversations,
    showOnlyStarredByBrandConversations,
    setShowOnlyUnreadConversations,
    setShowOnlyStarredByBrandConversations,
    setSearch,
    nextConversations,
    query,
    conversationsNextRequest,
  } = useMessengerStoreConversationListFields();

  const { getConversationIsUnread, getConversationOtherPartyFirstName } =
    useMessengerStoreGetConversation();
  const latestUnreadConversationToken = orderedConversationIdentifiers.find(
    (conversationIdentifier) => getConversationIsUnread(conversationIdentifier)
  );

  const latestUnreadConversationOtherPartyFirstName =
    latestUnreadConversationToken
      ? getConversationOtherPartyFirstName(latestUnreadConversationToken)
      : undefined;

  // Note: `...` spread syntax misses some fields here, not sure why, but we
  // list each field explicitly to prevent this.
  return {
    latestUnreadConversationOtherPartyFirstName,
    orderedConversationIdentifiers,
    hasConversations,
    isLoadingConversations,
    unreadConversationCount,
    setTotalUnreadConversations,
    starredByBrandConversationCount,
    showOnlyUnreadConversations,
    showOnlyStarredByBrandConversations,
    setShowOnlyUnreadConversations,
    setShowOnlyStarredByBrandConversations,
    setSearch,
    nextConversations,
    query,
    conversationsNextRequest,
  };
};

export const useMessengerStoreInit = getUseFields(["init"]);

// Conversation Management Hooks
export const useMessengerStoreConversationManagement = getUseFields([
  "setConversationIsStarredByBrand",
  "toggleConversationIsStarredByBrand",
  "hideConversation",
  "fetchConversationMessages",
  "sendMessageInConversation",
  "firstFetchOrUpdateConversations",
]);

// Context Management Hooks
export const useMessengerStoreUser = () => {
  const { user } = useMessengerStorePrivateFields();
  const isBrandUser = user?.type === IUser.Type.BRAND_USER;
  return useMemo(() => ({ isBrandUser }), [isBrandUser]);
};

export const useMessengerStoreBrandContext = getUseFields([
  "setCurrentBrandContext",
]);
export const useMessengerStoreRetailerContext = getUseFields([
  "currentRetailerContext",
  "updateCurrentRetailerContext",
]);

export const isConversationToken = (token: string): boolean => {
  return token.startsWith("mc_");
};

// Individual Conversation Hooks
export const useMessengerStoreGetConversation = () => {
  const { strictLocalize } = useStrictLocalization();
  const {
    getConversationByIdentifier,
    conversationByBrandToken,
    conversationByRetailerToken,
    storeUpdatedConversation,
    setTotalUnreadConversations,
  } = useMessengerStorePrivateFields();

  // This `useCallback` is here to prevent the function literal from being
  // over-reactive. Technically it's under-reactive, here, since
  // `conversationByConversationToken` is mutated and not re-created, but
  // because we capture it by-reference and the callback operates on it
  // only at call-time, it's actually fine.
  const hasConversation = useCallback(
    (conversationIdentifier: string) => {
      return !!getConversationByIdentifier(conversationIdentifier);
    },
    [getConversationByIdentifier]
  );

  const getConversationToken = useCallback(
    (conversationIdentifier: string) => {
      const conversation = getConversationByIdentifier(conversationIdentifier);
      return conversation ? getConversationModelToken(conversation) : undefined;
    },
    [getConversationByIdentifier]
  );

  const getConversationCreatedAt = useCallback(
    (conversationIdentifier: string) => {
      const conversation = getConversationByIdentifier(conversationIdentifier);
      return conversation
        ? getConversationModelCreatedAt(conversation)
        : undefined;
    },
    [getConversationByIdentifier]
  );

  const getConversationLatestMessage = useCallback(
    (conversationIdentifier: string): MessageModel | undefined => {
      const conversation = getConversationByIdentifier(conversationIdentifier);
      return conversation
        ? getConversationModelLatestMessage(conversation)
        : undefined;
    },
    [getConversationByIdentifier]
  );

  const getConversationRetailer = useCallback(
    (conversationIdentifier: string) => {
      return getConversationByIdentifier(conversationIdentifier)?.retailer;
    },
    [getConversationByIdentifier]
  );

  const getConversationBrand = useCallback(
    (conversationIdentifier: string) => {
      return getConversationByIdentifier(conversationIdentifier)?.brand;
    },
    [getConversationByIdentifier]
  );

  const getConversationOtherPartyType = useCallback(
    (conversationIdentifier: string) => {
      return getConversationByIdentifier(conversationIdentifier)?.otherParty
        ?.type;
    },
    [getConversationByIdentifier]
  );

  const getConversationOtherPartyUserToken = useCallback(
    (conversationIdentifier: string) => {
      return getConversationByIdentifier(conversationIdentifier)?.otherParty
        ?.token;
    },
    [getConversationByIdentifier]
  );

  const getConversationOtherPartyRetailerToken = useCallback(
    (conversationIdentifier: string) => {
      return getConversationByIdentifier(conversationIdentifier)?.otherParty
        ?.retailer_token;
    },
    [getConversationByIdentifier]
  );

  const getConversationOtherPartyBrandToken = useCallback(
    (conversationIdentifier: string) => {
      const conversation = getConversationByIdentifier(conversationIdentifier);
      return conversation
        ? getConversationModelOtherPartyBrandToken(conversation)
        : undefined;
    },
    [getConversationByIdentifier]
  );

  const getConversationIsUnread = useCallback(
    (conversationIdentifier: string) => {
      const conversation = getConversationByIdentifier(conversationIdentifier);
      return conversation ? isConversationModelUnRead(conversation) : false;
    },
    [getConversationByIdentifier]
  );

  const getConversationOtherPartyFirstName = useCallback(
    (conversationIdentifier: string) => {
      return getConversationByIdentifier(conversationIdentifier)?.otherParty
        ?.first_name;
    },
    [getConversationByIdentifier]
  );

  const getConversationOtherPartyBusinessImage = useCallback(
    (conversationIdentifier: string) => {
      const conversation = getConversationByIdentifier(conversationIdentifier);
      return conversation
        ? getConversationModelOtherPartyBusinessImage(conversation)
        : undefined;
    },
    [getConversationByIdentifier]
  );

  const getConversationIsSupportConversation = useCallback(
    (conversationIdentifier: string) => {
      const conversation = getConversationByIdentifier(conversationIdentifier);
      return conversation
        ? isConversationModelSupportConversation(conversation)
        : false;
    },
    [getConversationByIdentifier]
  );

  const getConversationIsMarkedAsSpam = useCallback(
    (conversationIdentifier: string) => {
      const conversation = getConversationByIdentifier(conversationIdentifier);
      return conversation
        ? isConversationModelMarkedAsSpam(conversation)
        : false;
    },
    [getConversationByIdentifier]
  );

  const getConversationIsStarredByBrand = useCallback(
    (conversationIdentifier: string) => {
      const conversation = getConversationByIdentifier(conversationIdentifier);
      return conversation
        ? isConversationModelStarredByBrand(conversation)
        : false;
    },
    [getConversationByIdentifier]
  );

  const getConversationUserToken = useCallback(
    (conversationIdentifier: string) => {
      const conversation = getConversationByIdentifier(conversationIdentifier);
      return conversation
        ? getConversationModelUserToken(conversation)
        : undefined;
    },
    [getConversationByIdentifier]
  );

  const getConversationOtherPartyCompanyName = useCallback(
    (conversationIdentifier: string) => {
      const conversation = getConversationByIdentifier(conversationIdentifier);
      if (!conversation) {
        return undefined;
      }

      let companyName = "";
      if (conversation.otherParty?.type === IUser.Type.ADMIN) {
        companyName = strictLocalize({
          defaultMessage: "Faire Support",
          description: {
            text: "Name of the other party when chatting with Faire Support",
          },
        });
      } else if (
        conversation.otherParty?.type === IUser.Type.USER &&
        conversation.retailer &&
        conversation.retailer.name
      ) {
        companyName = conversation.retailer.name;
      } else if (
        conversation.otherParty?.type === IUser.Type.BRAND_USER &&
        conversation.brand &&
        conversation.brand.name
      ) {
        companyName = conversation.brand.name;
      } else if (
        conversation.otherParty?.type === IUser.Type.GUEST &&
        conversation.otherPartyCompany
      ) {
        companyName = conversation.otherPartyCompany;
      }
      return companyName;
    },
    [getConversationByIdentifier, strictLocalize]
  );

  const getConversationIdentifier = useCallback(
    (conversationIdentifier: string) => {
      const conversation = getConversationByIdentifier(conversationIdentifier);
      if (!conversation) {
        return undefined;
      }

      return (
        getConversationModelToken(conversation) ??
        conversation.brand?.token ??
        conversation.retailer?.token ??
        getConversationModelUserToken(conversation)
      );
    },
    [getConversationByIdentifier]
  );

  const getConversationKey = useCallback(
    (conversationIdentifier: string) => {
      const conversation = getConversationByIdentifier(conversationIdentifier);
      return conversation ? getConversationModelKey(conversation) : undefined;
    },
    [getConversationByIdentifier]
  );

  const getConversationOtherParty = useCallback(
    (conversationIdentifier: string) => {
      return getConversationByIdentifier(conversationIdentifier)?.otherParty;
    },
    [getConversationByIdentifier]
  );

  const getConversationOtherPartyProfileImage = useCallback(
    (conversationIdentifier: string) => {
      return getConversationByIdentifier(conversationIdentifier)?.otherParty
        ?.profile_image;
    },
    [getConversationByIdentifier]
  );

  const getConversationOrderedMessages = useCallback(
    (conversationIdentifier: string) => {
      const conversation = getConversationByIdentifier(conversationIdentifier);
      return conversation
        ? getConversationModelOrderedMessages(conversation)
        : undefined;
    },
    [getConversationByIdentifier]
  );

  const getConversationIsInputDisabled = useCallback(
    (conversationIdentifier: string) => {
      const conversation = getConversationByIdentifier(conversationIdentifier);
      return conversation
        ? isConversationModelInputDisabled(conversation)
        : undefined;
    },
    [getConversationByIdentifier]
  );

  const getConversationIsOtherPartyBrandCustomer = useCallback(
    (conversationIdentifier: string) => {
      return getConversationByIdentifier(conversationIdentifier)
        ?.isOtherPartyBrandCustomer;
    },
    [getConversationByIdentifier]
  );

  const getConversationFetchAdditionalMessagesThrottled = useCallback(
    (conversationIdentifier: string) => {
      const conversation = getConversationByIdentifier(conversationIdentifier);
      return conversation
        ? () => fetchAdditionalMessagesThrottled(conversation)
        : undefined;
    },
    [getConversationByIdentifier]
  );

  const getConversationOtherPartyUserType = useCallback(
    (conversationIdentifier: string) => {
      const conversation = getConversationByIdentifier(conversationIdentifier);
      return conversation
        ? getConversationModelOtherPartyUserType(conversation)
        : undefined;
    },
    [getConversationByIdentifier]
  );

  const getConversationParticipants = useCallback(
    (conversationIdentifier: string) => {
      const conversation = getConversationByIdentifier(conversationIdentifier);
      return conversation
        ? getConversationModelParticipants(conversation)
        : undefined;
    },
    [getConversationByIdentifier]
  );

  const getConversationShouldShowLoadingSpinner = useCallback(
    (conversationIdentifier: string) => {
      const conversation = getConversationByIdentifier(conversationIdentifier);
      return conversation?.isLoading || !!conversation?.nextRequest;
    },
    [getConversationByIdentifier]
  );

  const getConversationFetchBrandCustomer = useCallback(
    (conversationIdentifier: string) => {
      const conversation = getConversationByIdentifier(conversationIdentifier);
      return conversation ? () => getBrandCustomer(conversation) : undefined;
    },
    [getConversationByIdentifier]
  );

  const getConversationOpenConversation = useCallback(
    (conversationIdentifier: string) => {
      const conversation = getConversationByIdentifier(conversationIdentifier);
      return conversation
        ? async () => {
            const [updatedConversation, totalUnreadConversations] =
              await openConversation(conversation);
            storeUpdatedConversation(updatedConversation);
            setTotalUnreadConversations(totalUnreadConversations);
            return updatedConversation;
          }
        : undefined;
    },
    [
      getConversationByIdentifier,
      setTotalUnreadConversations,
      storeUpdatedConversation,
    ]
  );

  const getConversationMessageAuthors = useCallback(
    (conversationIdentifier: string) => {
      return getConversationByIdentifier(conversationIdentifier)
        ?.messageAuthors;
    },
    [getConversationByIdentifier]
  );

  const hasConversationWithRetailer = useCallback(
    (retailerToken: string) => {
      return !!conversationByRetailerToken.get(retailerToken);
    },
    [conversationByRetailerToken]
  );

  const hasConversationWithBrand = useCallback(
    (brandToken: string) => {
      return !!conversationByBrandToken.get(brandToken);
    },
    [conversationByBrandToken]
  );

  return useMemo(
    () => ({
      hasConversation,
      getConversationToken,
      getConversationCreatedAt,
      getConversationLatestMessage,
      getConversationRetailer,
      getConversationBrand,
      getConversationOtherPartyType,
      getConversationOtherPartyUserToken,
      getConversationOtherPartyRetailerToken,
      getConversationOtherPartyBrandToken,
      getConversationIsUnread,
      getConversationOtherPartyFirstName,
      getConversationOtherPartyBusinessImage,
      getConversationIsSupportConversation,
      getConversationIsMarkedAsSpam,
      getConversationIsStarredByBrand,
      getConversationOtherPartyCompanyName,
      getConversationIdentifier,
      getConversationKey,
      getConversationOtherParty,
      getConversationUserToken,
      getConversationOtherPartyProfileImage,
      getConversationOrderedMessages,
      getConversationIsInputDisabled,
      getConversationIsOtherPartyBrandCustomer,
      getConversationFetchAdditionalMessagesThrottled,
      getConversationOtherPartyUserType,
      getConversationParticipants,
      getConversationShouldShowLoadingSpinner,
      getConversationFetchBrandCustomer,
      getConversationOpenConversation,
      getConversationMessageAuthors,
      isConversationToken,
      hasConversationWithRetailer,
      hasConversationWithBrand,
    }),
    [
      getConversationToken,
      getConversationBrand,
      getConversationCreatedAt,
      getConversationFetchAdditionalMessagesThrottled,
      getConversationFetchBrandCustomer,
      getConversationIdentifier,
      getConversationIsInputDisabled,
      getConversationIsMarkedAsSpam,
      getConversationIsOtherPartyBrandCustomer,
      getConversationIsStarredByBrand,
      getConversationIsSupportConversation,
      getConversationIsUnread,
      getConversationKey,
      getConversationLatestMessage,
      getConversationMessageAuthors,
      getConversationOpenConversation,
      getConversationOrderedMessages,
      getConversationOtherParty,
      getConversationOtherPartyBrandToken,
      getConversationOtherPartyBusinessImage,
      getConversationOtherPartyCompanyName,
      getConversationOtherPartyFirstName,
      getConversationOtherPartyProfileImage,
      getConversationOtherPartyRetailerToken,
      getConversationOtherPartyType,
      getConversationOtherPartyUserToken,
      getConversationOtherPartyUserType,
      getConversationParticipants,
      getConversationRetailer,
      getConversationShouldShowLoadingSpinner,
      getConversationUserToken,
      hasConversation,
      hasConversationWithBrand,
      hasConversationWithRetailer,
    ]
  );
};

export const useMessengerStoreLatestUnreadPopUpConversation = () => {
  // Note: this cannot currently be memoized because `conversationByUserToken`
  // is mutated.
  const { conversationByUserToken } = useMessengerStorePrivateFields();

  // Use traditional for loop to avoid property access issues
  const conversations = Array.from(conversationByUserToken?.values() ?? []);
  let latestUnreadPopUpConversation = null;

  for (const conversation of conversations) {
    const latestMessage = getConversationModelLatestMessage(conversation);
    const isFromOtherUser =
      latestMessage && isMessageModelFromOtherUser(latestMessage, conversation);
    if (
      latestMessage &&
      isFromOtherUser &&
      getMessageModelShouldPopUp(latestMessage) &&
      !isMessageModelRead(latestMessage, conversation)
    ) {
      if (!latestUnreadPopUpConversation) {
        latestUnreadPopUpConversation = conversation;
      } else {
        const currentModified =
          getConversationModelLastModified(conversation) || 0;
        const latestModified =
          getConversationModelLastModified(latestUnreadPopUpConversation) || 0;
        if (currentModified > latestModified) {
          latestUnreadPopUpConversation = conversation;
        }
      }
    }
  }

  const latestUnreadPopUpConversationConversationToken =
    latestUnreadPopUpConversation
      ? getConversationModelToken(latestUnreadPopUpConversation)
      : undefined;

  const {
    getConversationOtherPartyUserToken,
    getConversationOtherPartyBrandToken,
    getConversationOtherPartyRetailerToken,
  } = useMessengerStoreGetConversation();

  const latestUnreadPopUpConversationOtherPartyUserToken =
    latestUnreadPopUpConversationConversationToken
      ? getConversationOtherPartyUserToken(
          latestUnreadPopUpConversationConversationToken
        )
      : undefined;
  const latestUnreadPopUpConversationOtherPartyBrandToken =
    latestUnreadPopUpConversationConversationToken
      ? getConversationOtherPartyBrandToken(
          latestUnreadPopUpConversationConversationToken
        )
      : undefined;
  const latestUnreadPopUpConversationOtherPartyRetailerToken =
    latestUnreadPopUpConversationConversationToken
      ? getConversationOtherPartyRetailerToken(
          latestUnreadPopUpConversationConversationToken
        )
      : undefined;

  const hasUnreadPopUpConversation =
    !!latestUnreadPopUpConversationConversationToken;

  return useMemo(
    () => ({
      latestUnreadPopUpConversationConversationToken,
      latestUnreadPopUpConversationOtherPartyUserToken,
      latestUnreadPopUpConversationOtherPartyBrandToken,
      latestUnreadPopUpConversationOtherPartyRetailerToken,
      hasUnreadPopUpConversation,
    }),
    [
      latestUnreadPopUpConversationConversationToken,
      latestUnreadPopUpConversationOtherPartyUserToken,
      latestUnreadPopUpConversationOtherPartyBrandToken,
      latestUnreadPopUpConversationOtherPartyRetailerToken,
      hasUnreadPopUpConversation,
    ]
  );
};

// eslint-disable-next-line complexity
export const useMessengerStoreCurrentConversation = () => {
  const {
    currentConversationIdentifier,
    setCurrentConversation,
    updateCurrentConversationFromIdentifier,
    sendMessageInCurrentConversation,
  } = useMessengerStorePrivateFields();

  const {
    getConversationKey,
    getConversationToken,
    getConversationOtherPartyUserToken,
    getConversationOtherPartyBrandToken,
    getConversationOtherPartyRetailerToken,
    getConversationOtherParty,
    getConversationUserToken,
    getConversationBrand,
    getConversationRetailer,
    getConversationOtherPartyProfileImage,
    getConversationOtherPartyCompanyName,
    getConversationOtherPartyBusinessImage,
    getConversationLatestMessage,
    getConversationOrderedMessages,
    getConversationIsUnread,
    getConversationIsInputDisabled,
    getConversationIsMarkedAsSpam,
    getConversationIsOtherPartyBrandCustomer,
    getConversationIsStarredByBrand,
    getConversationFetchAdditionalMessagesThrottled,
    getConversationOtherPartyUserType,
    getConversationParticipants,
    getConversationOtherPartyFirstName,
    getConversationShouldShowLoadingSpinner,
    getConversationFetchBrandCustomer,
    getConversationOpenConversation,
    getConversationMessageAuthors,
  } = useMessengerStoreGetConversation();

  const hasCurrentConversation = !!currentConversationIdentifier;

  const currentConversationToken = currentConversationIdentifier
    ? getConversationToken(currentConversationIdentifier)
    : undefined;
  const currentConversationKey = currentConversationIdentifier
    ? getConversationKey(currentConversationIdentifier)
    : undefined;
  const currentConversationOtherPartyUserToken = currentConversationIdentifier
    ? getConversationOtherPartyUserToken(currentConversationIdentifier)
    : undefined;
  const currentConversationOtherPartyBrandToken = currentConversationIdentifier
    ? getConversationOtherPartyBrandToken(currentConversationIdentifier)
    : undefined;
  const currentConversationOtherPartyRetailerToken =
    currentConversationIdentifier
      ? getConversationOtherPartyRetailerToken(currentConversationIdentifier)
      : undefined;
  const currentConversationOtherParty = currentConversationIdentifier
    ? getConversationOtherParty(currentConversationIdentifier)
    : undefined;
  const currentConversationUserToken = currentConversationIdentifier
    ? getConversationUserToken(currentConversationIdentifier)
    : undefined;
  const currentConversationBrand = currentConversationIdentifier
    ? getConversationBrand(currentConversationIdentifier)
    : undefined;
  const currentConversationRetailer = currentConversationIdentifier
    ? getConversationRetailer(currentConversationIdentifier)
    : undefined;
  const currentConversationOtherPartyProfileImage =
    currentConversationIdentifier
      ? getConversationOtherPartyProfileImage(currentConversationIdentifier)
      : undefined;
  const currentConversationOtherPartyCompanyName = currentConversationIdentifier
    ? getConversationOtherPartyCompanyName(currentConversationIdentifier)
    : undefined;
  const currentConversationOtherPartyBusinessImage =
    currentConversationIdentifier
      ? getConversationOtherPartyBusinessImage(currentConversationIdentifier)
      : undefined;
  const currentConversationLatestMessage = currentConversationIdentifier
    ? getConversationLatestMessage(currentConversationIdentifier)
    : undefined;
  const currentConversationOrderedMessages = currentConversationIdentifier
    ? getConversationOrderedMessages(currentConversationIdentifier)
    : undefined;
  const currentConversationIsUnread = currentConversationIdentifier
    ? getConversationIsUnread(currentConversationIdentifier)
    : undefined;
  const currentConversationIsInputDisabled = currentConversationIdentifier
    ? getConversationIsInputDisabled(currentConversationIdentifier)
    : undefined;
  const currentConversationIsMarkedAsSpam = currentConversationIdentifier
    ? getConversationIsMarkedAsSpam(currentConversationIdentifier)
    : undefined;
  const currentConversationIsOtherPartyBrandCustomer =
    currentConversationIdentifier
      ? getConversationIsOtherPartyBrandCustomer(currentConversationIdentifier)
      : undefined;
  const currentConversationIsStarredByBrand = currentConversationIdentifier
    ? getConversationIsStarredByBrand(currentConversationIdentifier)
    : undefined;

  // We want the type of `currentConversationFetchAdditionalMessagesThrottled` to be
  // a function instead of function | undefined so that we don't change the hooks
  // interface and have to refactor consumers. Technically this `useMemo` is
  // under-reactive; `getConversationFetchAdditionalMessagesThrottled` doesn't
  // get recomputed when the conversation mapping changes. However, because it closes
  // over `this`, it accesses the conversation mapping by-reference, so it's
  // actually okay.
  const currentConversationFetchAdditionalMessagesThrottled =
    useCallback(() => {
      if (currentConversationIdentifier) {
        getConversationFetchAdditionalMessagesThrottled(
          currentConversationIdentifier
        )?.();
      }
    }, [
      currentConversationIdentifier,
      getConversationFetchAdditionalMessagesThrottled,
    ]);

  const currentConversationOtherPartyUserType = currentConversationIdentifier
    ? getConversationOtherPartyUserType(currentConversationIdentifier)
    : undefined;
  const currentConversationParticipants = currentConversationIdentifier
    ? getConversationParticipants(currentConversationIdentifier)
    : undefined;
  const currentConversationOtherPartyFirstName = currentConversationIdentifier
    ? getConversationOtherPartyFirstName(currentConversationIdentifier)
    : undefined;
  const currentConversationShouldShowLoadingSpinner =
    currentConversationIdentifier
      ? getConversationShouldShowLoadingSpinner(currentConversationIdentifier)
      : undefined;

  const currentConversationFetchBrandCustomer = useCallback(() => {
    if (currentConversationIdentifier) {
      getConversationFetchBrandCustomer(currentConversationIdentifier)?.();
    }
  }, [currentConversationIdentifier, getConversationFetchBrandCustomer]);

  const currentConversationOpenConversation = useCallback(() => {
    if (currentConversationIdentifier) {
      getConversationOpenConversation(currentConversationIdentifier)?.();
    }
  }, [currentConversationIdentifier, getConversationOpenConversation]);

  const currentConversationMessageAuthors = currentConversationIdentifier
    ? getConversationMessageAuthors(currentConversationIdentifier)
    : undefined;

  return useMemo(
    () => ({
      currentConversationIdentifier,
      currentConversationKey,
      currentConversationToken,
      currentConversationOtherPartyUserToken,
      currentConversationOtherPartyBrandToken,
      currentConversationOtherPartyRetailerToken,
      currentConversationOtherParty,
      currentConversationUserToken,
      currentConversationBrand,
      currentConversationRetailer,
      currentConversationOtherPartyProfileImage,
      currentConversationOtherPartyCompanyName,
      currentConversationOtherPartyBusinessImage,
      currentConversationLatestMessage,
      currentConversationOrderedMessages,
      currentConversationIsUnread,
      currentConversationIsInputDisabled,
      currentConversationIsMarkedAsSpam,
      currentConversationIsOtherPartyBrandCustomer,
      currentConversationIsStarredByBrand,
      currentConversationFetchAdditionalMessagesThrottled,
      currentConversationOtherPartyUserType,
      currentConversationParticipants,
      currentConversationOtherPartyFirstName,
      currentConversationShouldShowLoadingSpinner,
      currentConversationFetchBrandCustomer,
      currentConversationOpenConversation,
      currentConversationMessageAuthors,
      hasCurrentConversation,
      setCurrentConversation,
      updateCurrentConversationFromIdentifier,
      sendMessageInCurrentConversation,
    }),
    [
      currentConversationBrand,
      currentConversationFetchAdditionalMessagesThrottled,
      currentConversationFetchBrandCustomer,
      currentConversationIdentifier,
      currentConversationIsInputDisabled,
      currentConversationIsMarkedAsSpam,
      currentConversationIsOtherPartyBrandCustomer,
      currentConversationIsStarredByBrand,
      currentConversationIsUnread,
      currentConversationKey,
      currentConversationLatestMessage,
      currentConversationMessageAuthors,
      currentConversationOpenConversation,
      currentConversationOrderedMessages,
      currentConversationOtherParty,
      currentConversationOtherPartyBrandToken,
      currentConversationOtherPartyBusinessImage,
      currentConversationOtherPartyCompanyName,
      currentConversationOtherPartyFirstName,
      currentConversationOtherPartyProfileImage,
      currentConversationOtherPartyRetailerToken,
      currentConversationOtherPartyUserToken,
      currentConversationOtherPartyUserType,
      currentConversationParticipants,
      currentConversationRetailer,
      currentConversationShouldShowLoadingSpinner,
      currentConversationToken,
      currentConversationUserToken,
      hasCurrentConversation,
      sendMessageInCurrentConversation,
      setCurrentConversation,
      updateCurrentConversationFromIdentifier,
    ]
  );
};

export const useMessengerStorePrivateFields = getUseFields([
  "user",
  "brand",
  "getConversationByIdentifier",
  "conversationByUserToken",
  "conversationByConversationToken",
  "conversationByRetailerToken",
  "conversationByBrandToken",
  "currentConversationIdentifier",
  "setCurrentConversation",
  "updateCurrentConversationFromIdentifier",
  "sendMessageInCurrentConversation",
  "setTotalUnreadConversations",
  "storeUpdatedConversation",
]);

export const useMessengerStoreGetConversationOrUndefined = () => {
  const store = useMessengerStoreOrUndefined();
  const { conversationByBrandToken } = store ?? {};

  const hasConversationWithBrand = useCallback(
    (brandToken: string) => {
      return !!conversationByBrandToken?.get(brandToken);
    },
    [conversationByBrandToken]
  );

  return useMemo(
    () => ({ hasConversationWithBrand }),
    [hasConversationWithBrand]
  );
};

export const useMessengerStoreBrandContextOrUndefined = () => {
  const store = useMessengerStoreOrUndefined();
  const setCurrentBrandContext = useCallback(
    async (
      brand: IGetMessengerConversationsResponse.ISimpleBrand | undefined
    ) => {
      if (store) {
        return await store.setCurrentBrandContext(brand);
      }
      return undefined;
    },
    [store]
  );

  return useMemo(() => ({ setCurrentBrandContext }), [setCurrentBrandContext]);
};

export const useMessengerStoreConversationMessage = () => {
  const { strictLocalize } = useStrictLocalization();
  const { brand, getConversationByIdentifier } =
    useMessengerStorePrivateFields();

  const isMessageFromCurrentUser = useCallback(
    (message: MessageModel, conversationIdentifier: string): boolean => {
      const conversation = getConversationByIdentifier(conversationIdentifier);
      if (!conversation) {
        return false;
      }
      return isMessageModelFromCurrentUser(message, conversation);
    },
    [getConversationByIdentifier]
  );

  const isMessageFromOtherUser = useCallback(
    (message: MessageModel, conversationIdentifier: string): boolean => {
      const conversation = getConversationByIdentifier(conversationIdentifier);
      if (!conversation) {
        return false;
      }
      return isMessageModelFromOtherUser(message, conversation);
    },
    [getConversationByIdentifier]
  );

  const isMessageFromSameRetailerAsCurrentUser = useCallback(
    (message: MessageModel, conversationIdentifier: string): boolean => {
      const conversation = getConversationByIdentifier(conversationIdentifier);
      if (!conversation) {
        return false;
      }
      return isMessageModelFromSameRetailerAsCurrentUser(
        message,
        conversation,
        conversation.messageAuthors
      );
    },
    [getConversationByIdentifier]
  );

  const isMessageFromSameBrandAsCurrentUser = useCallback(
    (message: MessageModel, conversationIdentifier: string): boolean => {
      const conversation = getConversationByIdentifier(conversationIdentifier);
      if (!conversation) {
        return false;
      }
      return isMessageModelFromSameBrandAsCurrentUser(
        message,
        conversation,
        conversation.messageAuthors
      );
    },
    [getConversationByIdentifier]
  );

  const isMessageRead = useCallback(
    (message: MessageModel, conversationIdentifier: string): boolean => {
      const conversation = getConversationByIdentifier(conversationIdentifier);
      if (!conversation) {
        return false;
      }
      return isMessageModelRead(message, conversation);
    },
    [getConversationByIdentifier]
  );

  const isMessageLatestReadFromCurrentUser = useCallback(
    (message: MessageModel, conversationIdentifier: string): boolean => {
      const conversation = getConversationByIdentifier(conversationIdentifier);
      if (!conversation) {
        return false;
      }
      return isMessageModelLatestReadFromCurrentUser(message, conversation);
    },
    [getConversationByIdentifier]
  );

  const isMessageLatestDelivered = useCallback(
    (message: MessageModel, conversationIdentifier: string): boolean => {
      const conversation = getConversationByIdentifier(conversationIdentifier);
      if (!conversation) {
        return false;
      }
      return isMessageModelLatestDelivered(message, conversation);
    },
    [getConversationByIdentifier]
  );

  const sendMessage = useCallback(
    (message: MessageModel, conversationIdentifier: string): Promise<void> => {
      const conversation = getConversationByIdentifier(conversationIdentifier);
      if (!conversation) {
        return Promise.resolve();
      }
      return sendMessageModel(message, conversation);
    },
    [getConversationByIdentifier]
  );

  const deleteMessage = useCallback(
    (message: MessageModel, conversationIdentifier: string): Promise<void> => {
      const conversation = getConversationByIdentifier(conversationIdentifier);
      if (!conversation) {
        return Promise.resolve();
      }
      return (
        deleteMessageModel(
          conversation,
          getMessageModelToken(message),
          strictLocalize
        )
          // Cast to `Promise<void>` to satisfy the type checker
          .then(() => {})
      );
    },
    [getConversationByIdentifier, strictLocalize]
  );

  const getOtherPartyProfilePicUrl = useCallback(
    (
      message: MessageModel,
      conversationIdentifier: string
    ): string | undefined => {
      const conversation = getConversationByIdentifier(conversationIdentifier);
      if (!conversation) {
        return undefined;
      }
      return getMessageModelOtherPartyProfilePicUrl(message, conversation);
    },
    [getConversationByIdentifier]
  );

  const getCurrentPartyProfilePicUrl = useCallback(
    (
      _message: MessageModel,
      conversationIdentifier: string
    ): string | undefined => {
      const conversation = getConversationByIdentifier(conversationIdentifier);
      if (!conversation) {
        return undefined;
      }
      return getMessageModelCurrentPartyProfilePicUrl(conversation, brand);
    },
    [brand, getConversationByIdentifier]
  );

  const getMessageOtherPartyFirstName = useCallback(
    (
      message: MessageModel,
      conversationIdentifier: string
    ): string | undefined => {
      const conversation = getConversationByIdentifier(conversationIdentifier);
      if (!conversation) {
        return undefined;
      }
      return getMessageModelOtherPartyFirstName(message, conversation);
    },
    [getConversationByIdentifier]
  );

  return useMemo(
    () => ({
      isMessageFromCurrentUser,
      isMessageFromOtherUser,
      isMessageFromSameRetailerAsCurrentUser,
      isMessageFromSameBrandAsCurrentUser,
      isMessageRead,
      isMessageLatestReadFromCurrentUser,
      isMessageLatestDelivered,
      sendMessage,
      deleteMessage,
      getOtherPartyProfilePicUrl,
      getCurrentPartyProfilePicUrl,
      getMessageOtherPartyFirstName,
    }),
    [
      isMessageFromCurrentUser,
      isMessageFromOtherUser,
      isMessageFromSameRetailerAsCurrentUser,
      isMessageFromSameBrandAsCurrentUser,
      isMessageRead,
      isMessageLatestReadFromCurrentUser,
      isMessageLatestDelivered,
      sendMessage,
      deleteMessage,
      getOtherPartyProfilePicUrl,
      getCurrentPartyProfilePicUrl,
      getMessageOtherPartyFirstName,
    ]
  );
};
